import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger, MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { SessionService } from '../../core/services/session.service';
import { BranchService } from '../../core/services/branch.service';
import { UserService } from '../../core/services/user.service';
import {
  SessionPlan,
  SessionPurchase,
  AttendanceRecord,
  AttendanceReport,
  AttendanceReportRow,
  CreateSessionPlanRequest,
  UpdateSessionPlanRequest,
  RecordAttendanceRequest,
  BranchPriceInput,
  AdminCreatePurchaseRequest,
  Sport
} from '../../core/models/session.model';
import { User, SubUser } from '../../core/models/user.model';
import { Branch } from '../../core/models/branch.model';
import { RecordPaymentRequest, PAYMENT_METHODS } from '../../core/models/payment.model';
import { Html5Qrcode } from 'html5-qrcode';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ProofImageDialog } from '../../shared/proof-image-dialog/proof-image-dialog';
import { AuthService } from '../../core/services/auth.service';
import { UserRole } from '../../core/models/auth.model';

@Component({
  selector: 'app-sessions',
  imports: [
    CommonModule, FormsModule, Sidebar,
    MatTabsModule, MatAutocompleteModule
  ],
  templateUrl: './sessions.html',
  styleUrl: './sessions.scss',
})
export class Sessions implements OnInit, OnDestroy {
  // Tab state
  activeTab: 'plans' | 'purchases' | 'attendance' | 'reports' = 'plans';
  tabIndex = 0;

  // Common data
  sports: Sport[] = [];
  branches: Branch[] = [];
  paymentMethods = PAYMENT_METHODS;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  // Filters
  selectedSportId = '';
  selectedYear: number;
  selectedMonth: number;
  selectedStatus = '';
  selectedBranchId = '';

  // ── Plans Tab ───────────────────────────────
  plans: SessionPlan[] = [];
  showPlanModal = false;
  isEditingPlan = false;
  editingPlanId = '';
  isSavingPlan = false;
  planForm: CreateSessionPlanRequest = { sportId: '', name: '', sessionCount: 1, price: 0, branchPrices: [] };
  branchPriceInputs: { branchId: string; branchName: string; price: number | null }[] = [];

  // ── Purchases Tab ───────────────────────
  purchases: SessionPurchase[] = [];
  showPaymentModal = false;
  selectedPurchaseId = '';
  selectedPurchase: SessionPurchase | null = null;
  isSavingPayment = false;
  paymentForm: RecordPaymentRequest = {
    amount: 0,
    paymentMethod: 'Cash',
    paymentDate: new Date().toISOString().split('T')[0]
  };

  // ── Create Purchase Modal ──────────────────
  today = new Date();
  showCreatePurchaseModal = false;
  isSavingPurchase = false;
  purchaseUserSearch = '';
  purchaseUsers: User[] = [];
  purchaseSelectedUser: User | null = null;
  purchaseSubUsers: SubUser[] = [];
  purchaseSportId = '';
  purchasePlans: SessionPlan[] = [];
  purchaseForm: AdminCreatePurchaseRequest = {
    userId: '', sessionPlanId: '', branchId: '',
    year: new Date().getFullYear(), month: new Date().getMonth() + 1
  };

  // ── Attendance Tab ──────────────────────────
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  attendanceRecords: AttendanceRecord[] = [];
  showAttendanceModal = false;
  isSavingAttendance = false;
  activePurchases: SessionPurchase[] = [];
  subscriberSearch: string | SessionPurchase = '';
  selectedSportIdForAttendance = '';
  attendanceDateForFilter: string = new Date().toISOString().split('T')[0];
  attendanceForm: RecordAttendanceRequest = {
    userSessionPurchaseId: '',
    branchId: '',
    attendanceDate: new Date().toISOString().split('T')[0]
  };

  // ── QR Scanner ─────────────────────────────
  showScannerModal = false;
  scannerError = '';
  scannerSuccess = '';
  private html5Qrcode: Html5Qrcode | null = null;

  // ── Reports Tab ─────────────────────────────
  report: AttendanceReport | null = null;

  constructor(
    private sessionService: SessionService,
    private branchService: BranchService,
    private userService: UserService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {
    const now = new Date();
    this.selectedYear = now.getFullYear();
    this.selectedMonth = now.getMonth() + 1;
  }

  get isOwner(): boolean {
    return this.authService.currentUser()?.role === UserRole.Owner;
  }

  hasPermission(key: 'canViewPlans' | 'canEditPlans' | 'canViewSubscriptions' | 'canConfirmPayment' | 'canAddAttendance'): boolean {
    if (this.isOwner) return true;
    const user = this.authService.currentUser();
    if (!user || !user.permissions) return false;
    return user.permissions.some(p => p[key]);
  }

  get canViewPlans(): boolean { return this.hasPermission('canViewPlans'); }
  get canEditPlans(): boolean { return this.hasPermission('canEditPlans'); }
  get canViewSubscriptions(): boolean { return this.hasPermission('canViewSubscriptions'); }
  get canConfirmPayment(): boolean { return this.hasPermission('canConfirmPayment'); }
  get canAddAttendance(): boolean { return this.hasPermission('canAddAttendance'); }

  ngOnInit() {
    this.loadSports();
    this.loadBranches();
    this.loadPlans();
  }

  ngOnDestroy() {
    this.stopScanner();
  }

  // ── Common ──────────────────────────────────

  private readonly tabMap: ('plans' | 'purchases' | 'attendance' | 'reports')[] = ['plans', 'purchases', 'attendance', 'reports'];

  onTabChange(index: number) {
    this.tabIndex = index;
    const tab = this.tabMap[index];
    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';
    if (tab === 'plans') this.loadPlans();
    else if (tab === 'purchases') this.loadPurchases();
    else if (tab === 'attendance') this.loadAttendanceHistory();
    else if (tab === 'reports') this.loadReport();
  }

  loadSports() {
    this.sessionService.getSports().subscribe({
      next: (res) => { if (res.success && res.data) this.sports = res.data; },
      error: (err) => console.error('Error loading sports:', err)
    });
  }

  loadBranches() {
    this.branchService.getBranches().subscribe({
      next: (res) => { if (res.success && res.data) this.branches = res.data; },
      error: (err) => console.error('Error loading branches:', err)
    });
  }

  getMonthName(month: number): string {
    return new Date(2000, month - 1, 1).toLocaleString('en', { month: 'long' });
  }

  formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  clearMessages() {
    this.errorMessage = '';
    this.successMessage = '';
  }

  showSuccess(msg: string) {
    this.successMessage = msg;
    setTimeout(() => this.successMessage = '', 3000);
  }

  // ── Plans Tab ───────────────────────────────

  loadPlans() {
    this.isLoading = true;
    this.sessionService.getPlans(this.selectedSportId || undefined).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.data) this.plans = res.data;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.message || 'Failed to load plans';
      }
    });
  }

  openAddPlan() {
    this.showPlanModal = true;
    this.isEditingPlan = false;
    this.editingPlanId = '';
    this.planForm = { sportId: '', name: '', sessionCount: 1, price: 0, branchPrices: [] };
    this.initBranchPriceInputs();
    this.clearMessages();
  }

  openEditPlan(plan: SessionPlan) {
    this.showPlanModal = true;
    this.isEditingPlan = true;
    this.editingPlanId = plan.id;
    this.planForm = {
      sportId: plan.sportId,
      name: plan.name,
      sessionCount: plan.sessionCount,
      price: plan.price,
      branchPrices: []
    };
    this.initBranchPriceInputs(plan.branchPrices);
    this.clearMessages();
  }

  private initBranchPriceInputs(existingPrices?: { branchId: string; branchName: string; price: number }[]) {
    this.branchPriceInputs = this.branches.map(b => {
      const existing = existingPrices?.find(bp => bp.branchId === b.id);
      return {
        branchId: b.id,
        branchName: b.name,
        price: existing ? existing.price : null
      };
    });
  }

  closePlanModal() {
    this.showPlanModal = false;
    this.clearMessages();
  }

  savePlan() {
    if (!this.planForm.sportId || !this.planForm.name || !this.planForm.sessionCount || !this.planForm.price) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    const branchPrices: BranchPriceInput[] = this.branchPriceInputs
      .filter(bp => bp.price !== null && bp.price > 0)
      .map(bp => ({ branchId: bp.branchId, price: bp.price! }));

    this.planForm.branchPrices = branchPrices;

    this.isSavingPlan = true;
    this.clearMessages();

    if (this.isEditingPlan) {
      const updateReq: UpdateSessionPlanRequest = {
        name: this.planForm.name,
        sessionCount: this.planForm.sessionCount,
        price: this.planForm.price,
        branchPrices: branchPrices
      };
      this.sessionService.updatePlan(this.editingPlanId, updateReq).subscribe({
        next: (res) => {
          this.isSavingPlan = false;
          if (res.success) {
            this.closePlanModal();
            this.loadPlans();
            this.showSuccess('Plan updated successfully');
          }
        },
        error: (err) => {
          this.isSavingPlan = false;
          this.errorMessage = err.error?.message || 'Failed to update plan';
        }
      });
    } else {
      this.sessionService.createPlan(this.planForm).subscribe({
        next: (res) => {
          this.isSavingPlan = false;
          if (res.success) {
            this.closePlanModal();
            this.loadPlans();
            this.showSuccess('Plan created successfully');
          }
        },
        error: (err) => {
          this.isSavingPlan = false;
          this.errorMessage = err.error?.message || 'Failed to create plan';
        }
      });
    }
  }

  deletePlan(plan: SessionPlan) {
    if (!confirm(`Are you sure you want to deactivate "${plan.name}"?`)) return;

    this.sessionService.deletePlan(plan.id).subscribe({
      next: (res) => {
        if (res.success) {
          this.loadPlans();
          this.showSuccess('Plan deactivated successfully');
        }
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Failed to deactivate plan';
      }
    });
  }

  // ── Purchases Tab ───────────────────────────

  loadPurchases() {
    this.isLoading = true;
    this.sessionService.getPurchases(
      this.selectedSportId || undefined,
      this.selectedYear,
      this.selectedMonth,
      this.selectedStatus || undefined,
      this.selectedBranchId || undefined
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.data) this.purchases = res.data;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.message || 'Failed to load purchases';
      }
    });
  }

  openPaymentModal(purchase: SessionPurchase) {
    this.selectedPurchase = purchase;
    this.selectedPurchaseId = purchase.id;
    this.showPaymentModal = true;
    this.paymentForm = {
      amount: purchase.remainingBalance,
      paymentMethod: 'Cash',
      paymentDate: new Date().toISOString().split('T')[0]
    };
    this.clearMessages();
  }

  closePaymentModal() {
    this.showPaymentModal = false;
    this.selectedPurchase = null;
    this.clearMessages();
  }

  savePayment() {
    if (!this.paymentForm.amount || !this.paymentForm.paymentMethod) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isSavingPayment = true;
    this.clearMessages();

    this.sessionService.recordPayment(this.selectedPurchaseId, this.paymentForm).subscribe({
      next: (res) => {
        this.isSavingPayment = false;
        if (res.success) {
          this.closePaymentModal();
          this.loadPurchases();
          this.showSuccess('Payment recorded successfully');
        }
      },
      error: (err) => {
        this.isSavingPayment = false;
        this.errorMessage = err.error?.message || 'Failed to record payment';
      }
    });
  }

  // ── Create Purchase Modal ───────────────────

  openCreatePurchaseModal() {
    this.showCreatePurchaseModal = true;
    this.purchaseUserSearch = '';
    this.purchaseUsers = [];
    this.purchaseSelectedUser = null;
    this.purchaseSubUsers = [];
    this.purchaseSportId = '';
    this.purchasePlans = [];
    this.purchaseForm = {
      userId: '', sessionPlanId: '', branchId: '',
      year: this.selectedYear, month: this.selectedMonth,
      selectedDate: undefined
    };
    this.clearMessages();
  }

  closeCreatePurchaseModal() {
    this.showCreatePurchaseModal = false;
    this.clearMessages();
  }

  searchPurchaseUsers() {
    const search = this.purchaseUserSearch.trim();
    if (search.length < 2) {
      this.purchaseUsers = [];
      return;
    }
    this.userService.getAllUsers(search, undefined, 1, 20).subscribe({
      next: (res) => {
        if (res.success && res.data) this.purchaseUsers = res.data.users;
      }
    });
  }

  selectPurchaseUser(user: User) {
    this.purchaseSelectedUser = user;
    this.purchaseForm.userId = user.id;
    this.purchaseUserSearch = `${user.name} (${user.phoneNumber})`;
    this.purchaseUsers = [];
    delete this.purchaseForm.subUserId;

    this.purchaseSubUsers = [];
    this.userService.getSubUsers(user.id).subscribe({
      next: (res) => {
        if (res.success && res.data) this.purchaseSubUsers = res.data;
      }
    });
  }

  onPurchaseSportChange(sportId: string) {
    this.purchaseForm.sessionPlanId = '';
    this.purchasePlans = [];
    if (!sportId) return;
    this.sessionService.getPlans(sportId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.purchasePlans = res.data.filter(p => p.isActive);
        }
      }
    });
  }

  isSelectedPlanSingleSession(): boolean {
    const plan = this.purchasePlans.find(p => p.id === this.purchaseForm.sessionPlanId);
    return plan?.sessionCount === 1;
  }

  getPlanDisplayPrice(plan: SessionPlan): string {
    if (this.purchaseForm.branchId) {
      const bp = plan.branchPrices?.find(b => b.branchId === this.purchaseForm.branchId);
      if (bp) return `${plan.name} — ${bp.price.toFixed(0)} EGP`;
    }
    return `${plan.name} — ${plan.price.toFixed(0)} EGP`;
  }

  savePurchase() {
    if (!this.purchaseForm.userId || !this.purchaseForm.sessionPlanId || !this.purchaseForm.branchId) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    const payload: any = { ...this.purchaseForm };

    if (this.isSelectedPlanSingleSession()) {
      if (!payload.selectedDate) {
        this.errorMessage = 'Please select a session date';
        return;
      }
      const d = new Date(payload.selectedDate);
      payload.selectedDate = d.toISOString();
      payload.year = d.getFullYear();
      payload.month = d.getMonth() + 1;
    } else {
      if (!payload.year || !payload.month) {
        this.errorMessage = 'Please fill in all required fields';
        return;
      }
      delete payload.selectedDate;
    }

    this.isSavingPurchase = true;
    this.clearMessages();

    this.sessionService.adminCreatePurchase(payload).subscribe({
      next: (res) => {
        this.isSavingPurchase = false;
        if (res.success) {
          this.closeCreatePurchaseModal();
          this.loadPurchases();
          this.showSuccess('Subscription created successfully');
        }
      },
      error: (err) => {
        this.isSavingPurchase = false;
        this.errorMessage = err.error?.message || 'Failed to create subscription';
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'PendingPayment': return 'status-pending';
      case 'Expired': return 'status-expired';
      case 'Cancelled': return 'status-cancelled';
      default: return '';
    }
  }

  getPaymentStatusClass(status: string): string {
    switch (status) {
      case 'FullyPaid': return 'status-verified';
      case 'PartiallyPaid': return 'status-partial';
      case 'Unpaid': return 'status-unverified';
      default: return '';
    }
  }

  getProofUrl(relativePath: string): string {
    const baseUrl = environment.apiUrl.replace(/\/api$/, '');
    return `${baseUrl}${relativePath}`;
  }

  openProofModal(url: string) {
    this.dialog.open(ProofImageDialog, {
      data: this.getProofUrl(url),
      maxWidth: '95vw',
    });
  }

  // ── Attendance Tab ──────────────────────────

  loadAttendanceHistory() {
    this.isLoading = true;
    this.sessionService.getAttendanceHistory(
      undefined,
      this.selectedSportId || undefined,
      this.selectedYear,
      this.selectedMonth
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.data) this.attendanceRecords = res.data;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.message || 'Failed to load attendance';
      }
    });
  }

  openAttendanceModal() {
    this.showAttendanceModal = true;
    this.attendanceForm = {
      userSessionPurchaseId: '',
      branchId: '',
      attendanceDate: new Date().toISOString().split('T')[0]
    };
    this.subscriberSearch = '';
    this.activePurchases = [];
    this.selectedSportIdForAttendance = this.sports.length ? this.sports[0].id : '';
    this.attendanceDateForFilter = new Date().toISOString().split('T')[0];
    this.clearMessages();
    this.loadFilteredPurchases();
    setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
  }

  loadFilteredPurchases() {
    if (!this.selectedSportIdForAttendance || !this.attendanceDateForFilter) return;
    const date = new Date(this.attendanceDateForFilter);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    this.sessionService.getPurchases(this.selectedSportIdForAttendance, year, month)
      .subscribe(res => {
        this.activePurchases = res.success && res.data ? res.data : [];
        this.subscriberSearch = '';
        this.attendanceForm.userSessionPurchaseId = '';
      });
  }

  get filteredPurchases(): SessionPurchase[] {
    const raw = this.subscriberSearch;
    const query = (typeof raw === 'string' ? raw : '').toLowerCase();
    return this.activePurchases
      .filter(p => {
        const name = (p.subUserName || p.userName).toLowerCase();
        const phone = (p.userPhone || '').toLowerCase();
        return name.includes(query) || phone.includes(query);
      })
      .sort((a, b) => {
        const aExhausted = a.sessionsUsed >= a.totalSessions ? 1 : 0;
        const bExhausted = b.sessionsUsed >= b.totalSessions ? 1 : 0;
        return aExhausted - bExhausted || (a.subUserName || a.userName).localeCompare(b.subUserName || b.userName);
      });
  }

  displayPurchaseName = (p: SessionPurchase): string => {
    if (!p) return '';
    return p.subUserName ? `${p.userName} → ${p.subUserName}` : p.userName;
  };

  onPurchaseSelect(event: any) {
    const purchase: SessionPurchase = event.option.value;
    if (purchase.sessionsUsed >= purchase.totalSessions) return;
    this.attendanceForm.userSessionPurchaseId = purchase.id;
  }

  closeAutocompletePanel() {
    if (this.autoTrigger) this.autoTrigger.closePanel();
  }

  closeAttendanceModal() {
    this.showAttendanceModal = false;
    this.clearMessages();
  }

  private formatDateToString(d: string): string {
    return d;
  }

  saveAttendance() {
    this.attendanceForm.attendanceDate = this.attendanceDateForFilter;
    if (!this.attendanceForm.userSessionPurchaseId || !this.attendanceForm.branchId || !this.attendanceForm.attendanceDate) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isSavingAttendance = true;
    this.clearMessages();

    this.sessionService.recordAttendance(this.attendanceForm).subscribe({
      next: (res) => {
        this.isSavingAttendance = false;
        if (res.success) {
          this.closeAttendanceModal();
          this.loadAttendanceHistory();
          this.showSuccess('Attendance recorded successfully');
        }
      },
      error: (err) => {
        this.isSavingAttendance = false;
        this.errorMessage = err.error?.message || 'Failed to record attendance';
      }
    });
  }

  undoAttendance(record: AttendanceRecord) {
    if (!confirm(`Undo attendance for ${record.userName}?`)) return;

    this.sessionService.undoAttendance(record.id).subscribe({
      next: (res) => {
        if (res.success) {
          this.loadAttendanceHistory();
          this.showSuccess('Attendance undone successfully');
        }
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Failed to undo attendance';
      }
    });
  }

  // ── QR Scanner ─────────────────────────────

  openScanner() {
    this.showScannerModal = true;
    this.scannerError = '';
    this.scannerSuccess = '';
    setTimeout(() => this.startScanner(), 300);
  }

  closeScanner() {
    this.stopScanner();
    this.showScannerModal = false;
    this.scannerError = '';
    this.scannerSuccess = '';
  }

  private startScanner() {
    const element = document.getElementById('qr-reader');
    if (!element) return;

    this.html5Qrcode = new Html5Qrcode('qr-reader');
    this.html5Qrcode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => this.onQrCodeScanned(decodedText),
      () => {}
    ).catch((err: any) => {
      this.scannerError = 'Could not access camera. Please allow camera permissions.';
      console.error('Scanner error:', err);
    });
  }

  private stopScanner() {
    if (this.html5Qrcode) {
      this.html5Qrcode.stop().catch(() => {});
      this.html5Qrcode = null;
    }
  }

  private onQrCodeScanned(decodedText: string) {
    this.stopScanner();
    this.scannerError = '';

    try {
      const data = JSON.parse(decodedText);
      if (!data.purchaseId) {
        this.scannerError = 'Invalid QR code: missing purchase ID';
        return;
      }

      this.scannerSuccess = 'QR code scanned! Loading subscriber details...';

      this.sessionService.getPurchaseDetails(data.purchaseId).subscribe({
        next: (res) => {
          if (res.success && res.data) {
            const purchase = res.data;

            if (purchase.status !== 'Active') {
              this.scannerError = `This subscription is ${purchase.status}. Only active subscriptions can record attendance.`;
              this.scannerSuccess = '';
              return;
            }

            if (purchase.sessionsUsed >= purchase.totalSessions) {
              this.scannerError = 'No remaining sessions on this subscription.';
              this.scannerSuccess = '';
              return;
            }

            this.closeScanner();
            this.showAttendanceModal = true;
            this.activePurchases = [purchase];
            this.subscriberSearch = purchase;
            this.selectedSportIdForAttendance = purchase.sportId;
            this.attendanceDateForFilter = new Date().toISOString().split('T')[0];
            this.attendanceForm = {
              userSessionPurchaseId: purchase.id,
              branchId: purchase.branchId || '',
              attendanceDate: new Date().toISOString().split('T')[0]
            };
          } else {
            this.scannerError = 'Subscription not found.';
            this.scannerSuccess = '';
          }
        },
        error: (err) => {
          this.scannerError = err.error?.message || 'Failed to look up subscription.';
          this.scannerSuccess = '';
        }
      });
    } catch {
      this.scannerError = 'Invalid QR code format.';
    }
  }

  // ── Reports Tab ─────────────────────────────

  loadReport() {
    this.isLoading = true;
    this.sessionService.getAttendanceReport(
      this.selectedYear,
      this.selectedMonth,
      this.selectedSportId || undefined
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.data) this.report = res.data;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error?.message || 'Failed to load report';
      }
    });
  }

  exportReportCsv() {
    if (!this.report || !this.report.rows.length) return;

    const headers = ['User Name', 'Sub-User', 'Phone', 'Sport', 'Plan', 'Total Sessions', 'Attended', 'Remaining', 'Status'];
    const rows = this.report.rows.map(r => [
      r.userName, r.subUserName || '', r.userPhone, r.sportName, r.planName,
      r.totalSessions, r.attendanceCount, r.remainingSessions, r.status
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${this.selectedYear}-${this.selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
